import { editChallengeLead, fetchChallengeLead } from '@actions/challenges';
import {
    getChallengesIsPatching,
    getChallengesPatchError,
    getChallengesPatchSuccess,
} from '@selectors/challenges';
import useForm from '@components/form/hooks/useForm';
import usePrevious from 'lib/src/hooks/usePrevious';
import { DropdownOption } from 'lib/src/types/shared/DropdownOption';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChallengeLead } from 'src/types/shared/Challenge';
import useFetchChallengeLeads from './useFetchChallengeLeads';

const useEditChallengeLead = (challengeID: number, onSuccess: () => void) => {
    const dispatch = useDispatch();

    const { leads, isFetching, fetchError } = useFetchChallengeLeads(challengeID);
    const isPatching = useSelector(getChallengesIsPatching);
    const patchError = useSelector(getChallengesPatchError);
    const patchSuccess = useSelector(getChallengesPatchSuccess);

    const options = Object.values(leads).map(getAssessorOption);
    const leadID = Object.values(leads).find(lead => lead.userCurrentlyHoldsRole)?.id ?? null;

    const [formState, handleChange] = useForm({
        leadID,
        challengeID,
    });

    useEffect(() => {
        handleChange('leadID', leadID);
    }, [leadID]); // eslint-disable-line

    const handleSubmit = () => {
        dispatch(editChallengeLead(formState));
    };

    const prevSuccess = usePrevious(patchSuccess);
    useEffect(() => {
        if (patchSuccess && !prevSuccess) {
            onSuccess();
            dispatch(fetchChallengeLead(challengeID));
        }
    }, [prevSuccess, patchSuccess, dispatch, challengeID, onSuccess]);

    return {
        options,

        isFetching,
        fetchError,
        isPatching,
        patchError,
        patchSuccess,

        formState,
        handleChange,
        handleSubmit,
    };
};

const getAssessorOption = (lead: ChallengeLead): DropdownOption<number> => ({
    value: lead.id,
    label: `${lead.firstName} ${lead.lastName}`,
});

export default useEditChallengeLead;
